import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useCrypto } from '../contexts/CryptoContext';
import { exportUserData } from '../services/exportService';
import DeleteAccountModal from '../components/DeleteAccountModal';

const S = '#00F5A0';
const G = '#8892A4';

const CARD = {
  background: 'rgba(255,255,255,0.04)',
  backdropFilter: 'blur(12px)',
  WebkitBackdropFilter: 'blur(12px)',
  border: '1px solid rgba(255,255,255,0.07)',
  borderRadius: 14,
  padding: '24px',
  marginBottom: 16,
};

const FIELD = {
  background: 'rgba(255,255,255,0.04)',
  border: '1px solid rgba(255,255,255,0.08)',
  borderRadius: 8,
  padding: '10px 12px',
  fontSize: 14,
  color: '#fff',
};

const SECTION_LABEL = {
  fontSize: 11,
  fontWeight: 600,
  color: 'rgba(255,255,255,0.35)',
  textTransform: 'uppercase',
  letterSpacing: '0.1em',
  margin: '0 0 16px',
};

const SettingsPage = () => {
  const { user, logout } = useAuth();
  const { masterKey, isUnlocked, lockVault } = useCrypto();
  const navigate = useNavigate();
  const [exportLoading, setExportLoading] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const handleExport = async () => {
    setExportLoading(true);
    try {
      const result = await exportUserData({ userId: user.id, masterKey });

      if (!result.success) {
        if (result.error === 'rate_limited') {
          alert(`Export déjà effectué récemment. Prochain export disponible dans ${result.retryAfterHours}h.`);
        } else if (result.error === 'vault_locked') {
          alert('Déverrouille ton coffre avant d\'exporter tes données.');
        } else {
          alert('Une erreur est survenue lors de l\'export. Réessaie ou contacte le support.');
        }
        return;
      }

      const blob = new Blob([JSON.stringify(result.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `safeheir-export-${new Date().toISOString().split('T')[0]}.json`;
      a.click();
      URL.revokeObjectURL(url);

      if (result.data.errors && result.data.errors.length > 0) {
        alert(`Export généré. ${result.data.errors.length} élément(s) n'ont pas pu être déchiffré(s), voir la section "errors" du fichier.`);
      }
    } catch (err) {
      console.error('[handleExport] Erreur inattendue:', err);
      alert('Erreur inattendue. Réessaie ou contacte le support.');
    } finally {
      setExportLoading(false);
    }
  };

  const handleDeleteSuccess = async () => {
    lockVault?.();
    await logout();
    window.location.replace('/compte-supprime');
  };

  return (
    <div style={{ minHeight: '100vh', background: '#0A1628', color: '#fff', paddingTop: 80 }}>
      <div style={{ maxWidth: 800, margin: '0 auto', padding: '40px 40px' }}>

        <div style={{ marginBottom: 32 }}>
          <h1 style={{ fontSize: 24, fontWeight: 700, margin: 0 }}>Paramètres</h1>
          <p style={{ margin: '4px 0 0', color: G, fontSize: 14 }}>Gérez votre compte et vos préférences</p>
        </div>

        {/* Profil */}
        <section style={CARD}>
          <h2 style={SECTION_LABEL}>Profil</h2>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 200 }}>
              <label style={{ fontSize: 13, color: G, display: 'block', marginBottom: 6 }}>Nom complet</label>
              <div style={FIELD}>{user?.full_name || '—'}</div>
            </div>
            <div style={{ flex: 1, minWidth: 200 }}>
              <label style={{ fontSize: 13, color: G, display: 'block', marginBottom: 6 }}>Email</label>
              <div style={FIELD}>{user?.email || '—'}</div>
            </div>
          </div>
        </section>

        {/* Plan */}
        <section style={CARD}>
          <h2 style={SECTION_LABEL}>Mon plan</h2>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
            <div>
              <div style={{ fontWeight: 600, fontSize: 16 }}>{user?.is_premium ? 'Plan Pro' : 'Plan Gratuit'}</div>
              <div style={{ fontSize: 13, color: G, marginTop: 4 }}>
                {user?.is_premium
                  ? 'Accès illimité à toutes les fonctionnalités'
                  : "Jusqu'à 3 bénéficiaires — 20 éléments dans le coffre"}
              </div>
            </div>
            {!user?.is_premium && (
              <div style={{ background: 'rgba(0,245,160,0.06)', border: '1px solid rgba(0,245,160,0.15)', borderRadius: 8, padding: '10px 16px', fontSize: 13, color: S, fontWeight: 600 }}>
                Plan Pro — disponible prochainement
              </div>
            )}
          </div>
        </section>

        {/* Check-in */}
        <section style={CARD}>
          <h2 style={SECTION_LABEL}>Check-in de sécurité</h2>
          <p style={{ color: G, fontSize: 14, margin: '0 0 16px', lineHeight: 1.6 }}>
            Configurez la fréquence de vos check-ins et les délais avant notification de vos bénéficiaires.
          </p>
          <button onClick={() => navigate('/checkin')}
            style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, padding: '10px 18px', color: '#fff', fontSize: 13, cursor: 'pointer', fontWeight: 500 }}>
            Gérer le check-in →
          </button>
        </section>

        {/* Export données (Art. 20 RGPD) */}
        <section style={CARD}>
          <h2 style={SECTION_LABEL}>Mes données</h2>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600, fontSize: 15, marginBottom: 4 }}>Exporter mes données</div>
              <div style={{ fontSize: 13, color: G, lineHeight: 1.6 }}>
                Téléchargez une copie complète de vos données au format JSON (compte, coffre-fort, bénéficiaires). Droit à la portabilité — Art. 20 RGPD.
              </div>
            </div>
            <button
              onClick={handleExport}
              disabled={exportLoading || !isUnlocked}
              title={!isUnlocked ? 'Déverrouille ton coffre pour exporter tes données' : undefined}
              style={{
                background: 'rgba(0,245,160,0.08)', border: '1px solid rgba(0,245,160,0.2)',
                borderRadius: 8, padding: '10px 18px', color: S,
                fontSize: 13, cursor: exportLoading ? 'wait' : 'pointer', fontWeight: 600, whiteSpace: 'nowrap',
              }}
            >
              {exportLoading ? 'Export...' : '↓ Exporter (JSON)'}
            </button>
          </div>
        </section>

        {/* Zone danger — suppression */}
        <section style={{ ...CARD, border: '1px solid rgba(239,68,68,0.2)', marginTop: 32 }}>
          <h2 style={{ ...SECTION_LABEL, color: 'rgba(239,68,68,0.6)' }}>Zone de danger</h2>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600, fontSize: 15, marginBottom: 4 }}>Supprimer mon compte</div>
              <div style={{ fontSize: 13, color: G, lineHeight: 1.6 }}>
                Supprime définitivement votre compte et toutes vos données (coffre-fort, bénéficiaires, historique). Cette action est irréversible. Droit à l'effacement — Art. 17 RGPD.
              </div>
            </div>
            <button
              onClick={() => setIsDeleteModalOpen(true)}
              style={{
                background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)',
                borderRadius: 8, padding: '10px 18px', color: '#f87171',
                fontSize: 13, cursor: 'pointer', fontWeight: 600, whiteSpace: 'nowrap',
              }}
            >
              Supprimer mon compte
            </button>
          </div>
        </section>

        <div style={{ marginTop: 32, textAlign: 'center' }}>
          <button onClick={() => navigate('/dashboard')}
            style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.3)', cursor: 'pointer', fontSize: 13 }}>
            ← Retour au tableau de bord
          </button>
        </div>
      </div>

      <DeleteAccountModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onSuccess={handleDeleteSuccess}
      />
    </div>
  );
};

export default SettingsPage;
